import { AHButton, AHEmptyScreen, Box } from "arkh";
import { useMemo, useCallback, useLayoutEffect } from "react";
import { useFragment, useLazyLoadQuery, useSubscription } from "react-relay";
import { graphql } from "relay-runtime";
import { useIntlTokens } from "../../components/mobile/useIntlTokens";
import { notEmpty } from "../../utils";

import { KPIItem } from "./KPIItemTeam/KPIItemTeam";
import { KPITeamScreenSubscription } from "./__generated__/KPITeamScreenSubscription.graphql";
import { KPITeamScreen_team$key } from "./__generated__/KPITeamScreen_team.graphql";
import { KPITeamScreenQuery } from "./__generated__/KPITeamScreenQuery.graphql";
import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";
import { UserLoggedInNavigatorScreenProps } from "../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { withWebModalStack } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModalStack";
import { useNavigationHeaderOptions } from "../../common/navigationHeaderOptions";
import { FlatList } from "react-native";
import { FormattedMessage } from "react-intl";
import { KPITeamEmptyIllustration } from "./KPITeamEmptyIllustration";

export const KPITeamScreen = withWebModalStack(KPITeamScreen_, true);
function KPITeamScreen_(props: UserLoggedInNavigatorScreenProps<"KPITeam">) {
  const getOpts = useNavigationHeaderOptions();
  const intl_tokens = useIntlTokens();

  useLayoutEffect(() => {
    props.navigation.setOptions(
      getOpts({
        type: "CLOSE",
        onClose: () => props.navigation.goBack(),
        title: intl_tokens.KPIs,
        defaultContentStyle: true,
        more: [
          {
            icon: "settings",
            onPress: () =>
              props.navigation.navigateDeprecated("StoreAdmin", {
                screen: "KPIAdmin",
                params: {
                  teamId: props.route.params.teamId,
                  standalone: true,
                },
              }),

            label: intl_tokens.Admin,
          },
        ],
      })
    );
  }, [
    getOpts,
    intl_tokens.Admin,
    intl_tokens.Edit,
    intl_tokens.KPIs,
    props.navigation,
    props.route.params.teamId,
  ]);
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fk) => (
        <KPITeamScreenC
          fetchKey={fk}
          teamId={props.route.params.teamId}
          onRequestCreateKPI={() => {
            props.navigation.navigateDeprecated("KPICreate", {});
          }}
          onPress={(args) => {
            props.navigation.navigateDeprecated("KPI", {
              routineId: args.routineId,
              routineTitle: args.routineTitle,
              sequenceNumber: args.sequenceNumber,
            });
          }}
        />
      )}
    </LazyQueryNetworkBoundrary>
  );
}
function KPITeamScreenC(props: {
  fetchKey: string;
  teamId: string;
  onRequestCreateKPI: () => void;
  onPress: (args: {
    routineId: string;
    routineTitle: string;
    sequenceNumber: number;
  }) => void;
}) {
  const data = useLazyLoadQuery<KPITeamScreenQuery>(
    graphql`
      query KPITeamScreenQuery($teamId: ID!) {
        store {
          team(id: $teamId) {
            ...KPITeamScreen_team
          }
        }
      }
    `,
    {
      teamId: props.teamId,
    },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  if (!data.store.team) {
    return null;
  }
  return (
    <KPITeam
      team={data.store.team}
      onPress={props.onPress}
      onRequestCreateKPI={props.onRequestCreateKPI}
    />
  );
}

function KPITeam(props: {
  team: KPITeamScreen_team$key;
  onPress: (args: {
    routineId: string;
    routineTitle: string;
    sequenceNumber: number;
  }) => void;
  onRequestCreateKPI: () => void;
}) {
  const data = useFragment(
    graphql`
      fragment KPITeamScreen_team on Team {
        id
        KPIS: routines(type: KPI) {
          id
          title
          ...KPIItemTeam_Routine
        }
      }
    `,
    props.team
  );

  useSubscription<KPITeamScreenSubscription>(
    useMemo(
      () => ({
        variables: {
          routineIds: data?.KPIS?.filter(notEmpty).map((r) => r.id) ?? [],
        },
        subscription: graphql`
          subscription KPITeamScreenSubscription($routineIds: [ID!]!) {
            routineTaskExecutionChanged(routineIds: $routineIds) {
              id
              ... on TaskExecutionNumber {
                isCompleted
                numberValue
                formattedNumberValue
              }
            }
          }
        `,
      }),
      [data?.KPIS]
    )
  );

  const KPIs = useMemo(
    () => data.KPIS?.slice().sort((a, b) => (a.title > b.title ? 1 : -1)) ?? [],
    [data.KPIS]
  );

  const { onPress } = props;

  const kpiItemOnPress = useCallback(
    (routineId: string, routineTitle: string, sequenceNumber: number) => {
      onPress({ routineId, routineTitle, sequenceNumber });
    },
    [onPress]
  );

  const renderItem = useCallback(
    ({ item }: { item: (typeof KPIs)[number] }) => {
      return (
        <Box space="medium" spaceBottom="none" key={item.id}>
          <KPIItem
            spaceTop="none"
            spaceLeft="none"
            spaceRight="small"
            key={item?.id}
            onPress={kpiItemOnPress}
            routine={item}
          />
        </Box>
      );
    },
    [kpiItemOnPress]
  );

  if (KPIs.length === 0) {
    return (
      <AHEmptyScreen
        title={
          <FormattedMessage
            defaultMessage="Let’s start with KPIs!"
            description="KPITeam Empty Screen Title"
          />
        }
        subtitle={
          <FormattedMessage
            defaultMessage="Key performance indicators provide a quick look at things like 'wastage' and 'shelf gaps,' making it easy to pinpoint areas for improvement."
            description="KPITeam Empty Screen Sub Title"
          />
        }
        illustration={<KPITeamEmptyIllustration />}
        cta={
          <>
            <AHButton
              iconRight
              iconType="KPI"
              onPress={() => {
                props.onRequestCreateKPI();
              }}
              themeType="primary"
              title={
                <FormattedMessage
                  description="KPITeam Empty Screen CTA"
                  defaultMessage="Create KPI"
                />
              }
            />
          </>
        }
      />
    );
  }

  return <FlatList data={KPIs} renderItem={renderItem} />;
}
